import React from 'react';

import { UxContext } from '@client/context/UxContext';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { type DateFormat, type TimeFormat } from '@ledium/types';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { Divider, FormControl, Grid, InputLabel, LinearProgress, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

interface UpdatingStatus {
    downloading: boolean;
    percent?: number;
    speedKb?: number;
    eta?: number;
}

interface ApplicationTabProps {
    helperUnavailable: boolean;
    serverOffline: boolean;
    initialNightlyBuild: boolean;
    initialEnableNightlyBuildInstalls: boolean;
    onBlock: (text: string | null) => void;
}

function ApplicationTab({ helperUnavailable, serverOffline, initialNightlyBuild, initialEnableNightlyBuildInstalls, onBlock }: ApplicationTabProps) {
    const {
        globalState: { socket, dateFormat: savedDateFormat, timeFormat: savedTimeFormat },
        globalActions
    } = useGlobalStore();
    const { handleSnackbar } = React.useContext(UxContext)!;

    const [currentVersion, setCurrentVersion] = React.useState<string | null>(null);
    const [currentVersionIsNightly, setCurrentVersionIsNightly] = React.useState(false);
    const [remoteVersion, setRemoteVersion] = React.useState<string | null>(null);
    const [remoteVersionIsNightly, setRemoteVersionIsNightly] = React.useState(false);
    const [updating, setUpdating] = React.useState<UpdatingStatus | false>(false);
    const [nightlyBuild, setNightlyBuild] = React.useState(false);
    const [enableNightlyBuildInstalls, setEnableNightlyBuildInstalls] = React.useState(false);
    const [dateFormat, setDateFormat] = React.useState<DateFormat>(savedDateFormat ?? 'YYYY-MM-DD');
    const [timeFormat, setTimeFormat] = React.useState<TimeFormat>(savedTimeFormat ?? '24');
    const dateTimeSettingsDirty = dateFormat !== (savedDateFormat ?? 'YYYY-MM-DD') || timeFormat !== (savedTimeFormat ?? '24');

    React.useEffect(() => {
        setNightlyBuild(initialNightlyBuild);
    }, [initialNightlyBuild]);

    React.useEffect(() => {
        setEnableNightlyBuildInstalls(initialEnableNightlyBuildInstalls);
    }, [initialEnableNightlyBuildInstalls]);

    React.useEffect(() => {
        const handleUpdating = (status: UpdatingStatus) => {
            setUpdating(status);
            if (!status.downloading) {
                onBlock(i18n('installing_update') as string);
            }
        };
        socket!.on('updating', handleUpdating);
        socket!.on('update_done', handleUpdating);
        return () => {
            socket!.off('updating');
            socket!.off('update_done');
        };
    }, [socket, onBlock]);

    const fetchLatestVersion = React.useCallback(async () => {
        setRemoteVersion(null);
        const data = await lediumApi.get<{ latestVersion: string; nightly: boolean }>('update/getLatestVersion');
        setRemoteVersion(data?.latestVersion ?? '');
        setRemoteVersionIsNightly(data?.nightly ?? false);
    }, []);

    React.useEffect(() => {
        lediumApi.get<{ version: string; isNightly: boolean }>('update/getVersion').then((data) => {
            if (data) {
                setCurrentVersion(data.version);
                setCurrentVersionIsNightly(data.isNightly);
            }
        });
        fetchLatestVersion();
    }, [fetchLatestVersion]);

    function update() {
        if (remoteVersionIsNightly) {
            return lediumApi.get(`update/download/nightly/${remoteVersion}`);
        }
        return lediumApi.get(`update/download/${remoteVersion}`);
    }

    function onDateTimeFormatSave() {
        lediumApi.patch<{ success?: boolean }>('settings/dateTimeFormat', { dateFormat, timeFormat }).then((data) => {
            if (data?.success === true) {
                globalActions.api.setDateTimeFormat(dateFormat, timeFormat);
                handleSnackbar({ message: i18n('date_time_format_saved') as string });
            }
        });
    }

    async function onEnableNightlyBuildInstallsToggle(event: React.ChangeEvent<HTMLInputElement>) {
        const enabled = event.target.checked;
        setEnableNightlyBuildInstalls(enabled);
        const result = await lediumApi.patch('settings/enableNightlyBuildInstalls', { enabled });
        if (result) {
            handleSnackbar({ message: i18n('settings_saved') as string });
        } else {
            handleSnackbar({ message: i18n('settings_save_error') as string, severity: 'error' });
        }
        fetchLatestVersion();
    }

    const updateDisabledSection = helperUnavailable || serverOffline ? { opacity: 0.5, pointerEvents: 'none' as const } : undefined;

    const SectionOfflineIcon = () => (serverOffline ? <WifiOffIcon sx={{ color: 'error.main', fontSize: '1.2em' }} /> : null);
    const SectionWarningIcon = () => (helperUnavailable ? <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1.2em' }} /> : null);

    const Updating = () =>
        updating && updating.downloading ? (
            <>
                <div style={{ width: '50%', display: 'inline-block' }}>
                    <LinearProgress variant="determinate" value={updating.percent ?? 0} />
                </div>
                <Typography variant="body2">{i18n('update_in_progress', updating.percent, updating.speedKb, updating.eta)}</Typography>
            </>
        ) : (
            <Typography variant="body2">{i18n('update_downloaded')}</Typography>
        );

    const IsUpdateAvailable = () => {
        if (!remoteVersion) {
            return <>{i18n('no_update_available')}</>;
        }
        if (!enableNightlyBuildInstalls && remoteVersionIsNightly) {
            return <>{i18n('no_update_available')}</>;
        }
        const toSort = (v: string) => {
            const [a, b, c] = v.split('.').map(Number);
            return a * 1000000 + b * 1000 + c;
        };
        const remoteSort = toSort(remoteVersion);
        const currentSort = toSort(currentVersion ?? '0.0.0');
        if (remoteSort < currentSort) {
            return <>{i18n('no_update_available')}</>;
        }
        if (remoteSort === currentSort) {
            if (remoteVersionIsNightly || !currentVersionIsNightly) {
                return <>{i18n('no_update_available')}</>;
            }
        }
        const label = remoteVersionIsNightly ? `${i18n('update_to_version', remoteVersion)} Nightly` : i18n('update_to_version', remoteVersion);
        return (
            <Button
                variant="contained"
                onClick={update}
                sx={remoteVersionIsNightly ? { backgroundColor: 'warning.main', '&:hover': { backgroundColor: 'warning.dark' } } : undefined}
            >
                {label}
            </Button>
        );
    };

    const SearchingForUpdate = () => (
        <Typography variant="body2">{remoteVersion === null ? <>{i18n('searching_for_update')}</> : <IsUpdateAvailable />}</Typography>
    );

    return (
        <>
            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SectionOfflineIcon />
                        <SectionWarningIcon />
                        <Typography variant="h2" color={helperUnavailable || serverOffline ? 'text.secondary' : undefined}>
                            {i18n('software_update')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('software_update_description')}
                    </Typography>
                </Grid>

                <Grid item xs={12} sx={updateDisabledSection}>
                    <Typography variant="body2">{currentVersion ? i18n('application_version', currentVersion) : null}</Typography>
                </Grid>

                <Grid item xs={12} sx={updateDisabledSection}>
                    {updating ? <Updating /> : <SearchingForUpdate />}
                </Grid>

                {nightlyBuild && (
                    <Grid item xs={12} sx={{ textAlign: 'center', ...updateDisabledSection }}>
                        <FormControlLabel
                            control={<Switch checked={enableNightlyBuildInstalls} onChange={onEnableNightlyBuildInstallsToggle} color="primary" />}
                            label={i18n('enable_nightly_build_installs') as string}
                        />
                    </Grid>
                )}
            </Grid>

            <Divider sx={{ marginTop: '32px' }} />

            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Typography variant="h2">{i18n('date_and_time')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('date_and_time_description')}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Grid container spacing={3} sx={{ maxWidth: '50%', mx: 'auto' }}>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>{i18n('date_format')}</InputLabel>
                                <Select value={dateFormat} label={i18n('date_format')} onChange={(e) => setDateFormat(e.target.value as typeof dateFormat)}>
                                    <MenuItem value="YYYY-MM-DD">{i18n('date_format_ymd')}</MenuItem>
                                    <MenuItem value="MM/DD/YYYY">{i18n('date_format_mdy')}</MenuItem>
                                    <MenuItem value="DD/MM/YYYY">{i18n('date_format_dmy')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>{i18n('time_format')}</InputLabel>
                                <Select value={timeFormat} label={i18n('time_format')} onChange={(e) => setTimeFormat(e.target.value as typeof timeFormat)}>
                                    <MenuItem value="24">{i18n('time_format_24')}</MenuItem>
                                    <MenuItem value="12">{i18n('time_format_12')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Button variant="contained" color="primary" disabled={!dateTimeSettingsDirty} onClick={onDateTimeFormatSave}>
                        {i18n('save_changes')}
                    </Button>
                </Grid>
            </Grid>
        </>
    );
}

export default ApplicationTab;
